import {ApplyActionsResult, DocumentService, PartLibrary} from "@buildwithflux/core";
import {ClientFunctionsAdapter} from "@buildwithflux/firebase-functions-adapter";
import {
    CameraMode,
    ChatMessageContext,
    CopilotModel,
    CopilotPromptVersion,
    defaultSelectedContexts,
    EditorModes,
    FilteredReviewCategory,
    IChatMessageData,
    IChatMessageExecutionIntentMetaData,
    IChatMessageHumanMetaData,
    IChatMessageThread,
    IChatMessageThreadData,
    IKnowledgeBaseSuggestion,
    KnowledgeBaseContext,
    MessageReaction,
} from "@buildwithflux/models";
import {ChatRepository, DocumentRepository, KnowledgeBaseRepository} from "@buildwithflux/repositories";
import {areWeTestingWithJest, arrayToMapByUid, guid, isDevEnv, Logger, Unsubscriber} from "@buildwithflux/shared";
import type {Draft} from "mutative";
import {values} from "lodash";
import {StoreApi, UseBoundStore} from "zustand";
import {devtools} from "zustand/middleware";
import {createWithEqualityFn} from "zustand/traditional";
import {mutative} from "zustand-mutative";

import {getDefaultModel} from "components/pages/document/components/editor/components/right_drawer/copilot_chat/helpers/getDefaultModel";
import {getToolsFromMessage} from "components/pages/document/components/editor/components/right_drawer/copilot_chat/helpers/getToolsFromMessage";
import {replaceEmptyKeys} from "helpers/replaceEmptyKeys";
import {CurrentUserService} from "modules/auth";
import type {DocumentStore} from "modules/document/state/document";
import type {UseFeatureFlagsStore} from "modules/feature_flags/FeatureFlagsStore";

import {executeMessageActions} from "./actions/executeMessageActions";
import {handleBackendUpdatedThreads} from "./actions/handleBackendUpdatedThreads";
import {
    approveKnowledgeBaseSuggestion,
    declineKnowledgeBaseSuggestion,
    fetchKnowledgeBaseSuggestions,
    subscribeToKnowledgeBaseSuggestions,
    updateKnowledgeBaseSuggestion,
} from "./actions/knowledgeBaseSuggestions";
import {
    branchMessage,
    createMessage,
    deleteMessage,
    resetActionExecution,
    setActionExecuted,
    setActionExecuting,
} from "./actions/messages";
import {removeMessageReaction, setMessageReaction} from "./actions/reactions";
import {setCurrentDocumentUid} from "./actions/setCurrentDocumentUid";
import {setIsFluxOrganizationMember} from "./actions/setIsFluxOrganizationMember";
import {setKnowledgeBaseSuggestionsSubscription} from "./actions/setKnowledgeBaseSuggestionsSubscription";
import {
    createThread,
    deleteThread,
    setCurrentThread,
    setCurrentThreadToLatest,
    updateThreadName,
} from "./actions/threads";
import {updatedUserContext} from "./actions/updatedUserContext";
import {isUntitledThreadName, threadNameFromMessageText} from "./helpers/threadNameHelpers";
import {createIsThreadStreamingSelector} from "./selectors/createIsThreadStreamingSelector";
import {createSortedThreadMessagesSelector} from "./selectors/createSortedThreadMessagesSelector";

type NewMessageState = {
    text: string;
    model: CopilotModel;
    selectedContexts: ChatMessageContext[];
    focused: boolean;
    promptVersion?: CopilotPromptVersion;
    useSkipper?: boolean;
    useOrbit?: boolean;
};

export interface ICopilotChatStoreDependencies {
    documentService: DocumentService;
    documentStore: DocumentStore;
    documentRepository: DocumentRepository;
    chatRepository: ChatRepository;
    knowledgeBaseRepository: KnowledgeBaseRepository;
    logger: Logger;
    partLibrary: PartLibrary;
    currentUserService: CurrentUserService;
    functionsAdapter: ClientFunctionsAdapter;
    useFeatureFlagsStore: UseFeatureFlagsStore;
}

type CopilotChatStoreApi = {
    setCurrentDocumentUid: (documentUid: string) => void;
    setIsFluxOrganizationMember: (isFluxOrganizationMember: boolean) => void;

    setCurrentThread: (threadUid?: string) => void;
    setCurrentThreadLoaded: (loaded: boolean) => void;
    setCurrentThreadToLatest: () => void;
    setPendingThreadFromUrl: (threadUid: string | undefined) => void;
    clearPendingThreadFromUrl: () => void;
    setPendingMessageFromUrl: (messageUid: string | undefined) => void;
    clearPendingMessageFromUrl: () => void;

    createThread: (
        uid?: string,
        initialMessageText?: string,
        planningDisabled?: boolean,
    ) => Promise<string | undefined>; // returns new thread uid
    updateThreadName: (threadUid: string, name: string) => Promise<void>; // TODO: this is not used
    deleteThread: (threadUid: string) => Promise<void>;
    setThreads: (threads: IChatMessageThread[]) => void; // Set all threads

    createMessage: (
        threadUid: string,
        content: string,
        metaData: IChatMessageHumanMetaData | IChatMessageExecutionIntentMetaData,
    ) => Promise<IChatMessageData["uid"] | undefined>;
    branchMessage: (
        threadUid: string,
        messageUid: string,
        content?: string,
        metaData?: Partial<IChatMessageHumanMetaData>,
    ) => Promise<void>;
    deleteMessage: (threadUid: string, messageUid: string) => Promise<void>;
    resetActionExecution: (threadUid: string, messageUid: string) => Promise<void>;
    setActionExecuting: (threadUid: string, messageUid: string) => Promise<void>;
    setActionCompleted: (threadUid: string, messageUid: string) => Promise<void>;
    executeMessageActions: (threadUid: string, messageUid: string) => Promise<ApplyActionsResult | undefined>;

    // Drafting a message
    setDraftMessage: (threadUid: string, text: string) => void;
    setDraftMessageModel: (threadUid: string, model: CopilotModel) => void;
    setDraftMessageContexts: (threadUid: string, contexts: ChatMessageContext[] | undefined) => void;
    setDraftMessageFocused: (threadUid: string, focused: boolean) => void;
    setDraftMessagePromptVersion: (threadUid: string, promptVersion: CopilotPromptVersion) => void;

    stopMessageStreaming: (threadUid: string) => Promise<void>;

    makeCurrentMessageBranch: (threadUid: string, messageUid: string) => void;

    setMessageReaction: (threadUid: string, messageUid: string, reaction: MessageReaction) => Promise<void>;
    removeMessageReaction: (threadUid: string, messageUid: string) => Promise<void>;

    fetchKnowledgeBaseSuggestions: () => Promise<void>;
    subscribeToKnowledgeBaseSuggestions: () => Unsubscriber | undefined;
    setKnowledgeBaseSuggestionsSubscription: (subscription: Unsubscriber | null) => void;
    approveKnowledgeBaseSuggestion: (suggestionUid: string) => Promise<void>;
    declineKnowledgeBaseSuggestion: (suggestionUid: string) => Promise<void>;
    updateKnowledgeBaseSuggestion: (
        suggestionUid: string,
        knowledgeSuggestion: string,
        useWhen?: string,
        context?: KnowledgeBaseContext,
        wasEdited?: boolean,
    ) => Promise<void>;

    setNewMessageInput(params: Partial<NewMessageState>): void;
    handleUserClickedExamplePrompt(prompt: string): void;

    updatedUserContext(context: Partial<UserContextForCopilot>): void;
    sendCurrentMessage(): Promise<string | undefined>; // returns new thread id, when appropriate

    handleBackendUpdatedThreads(threads: IChatMessageThreadData[]): void;

    clear(): void;
};

/**
 * Represents the message in the user text input at the bottom of a thread, before the user has sent it
 */
type DraftMessage = {
    text: string;
    model?: CopilotModel;
    selectedContexts?: ChatMessageContext[];
    focused?: boolean;
    promptVersion?: CopilotPromptVersion;
};
type DraftMessages = {[threadUid: string]: DraftMessage};

export interface ICopilotChatStore {
    isShowingWelcomeMessage: boolean;
    isCreatingFirstMessage: boolean;

    newMessageInput: NewMessageState;
    userContext: UserContextForCopilot;

    // navbar
    navbarTitle: string | undefined;
    canCreateThread: boolean;

    currentDocumentUid?: string;
    isFluxOrganizationMember: boolean;
    currentThreadUid?: string;
    pendingThreadUidFromUrl?: string;

    threads: {[threadUid: string]: IChatMessageThread};
    draftMessages: DraftMessages; // messages the user is still typing
    threadsLoaded: boolean;
    currentThreadLoaded: boolean;

    // Deep linking: pending message UID from URL to scroll to
    pendingMessageUidFromUrl?: string;

    knowledgeBaseSuggestions: IKnowledgeBaseSuggestion[];
    knowledgeBaseSuggestionsLoading: boolean;
    knowledgeBaseSuggestionsSubscription: Unsubscriber | null;

    threadsSubscription?: Unsubscriber | undefined;
    threadMessagesSubscription?: Unsubscriber | undefined;
    threadMessagesReactionsSubscription: {[messageUid: string]: Unsubscriber | undefined};
}

export type CopilotChatStoreState = CopilotChatStoreApi & ICopilotChatStore;

export type CopilotChatStoreSetter = (
    // Note: copied from auto-generated type docs in IDE
    nextStateOrUpdater: Partial<CopilotChatStoreState> | ((state: Draft<CopilotChatStoreState>) => void),
    shouldReplace?: boolean | undefined,
    action?: string | undefined,
) => void;
export type CopilotChatStoreGetter = () => CopilotChatStoreState;

export type UserContextForCopilot = {
    editorMode: EditorModes;
    cameraMode: CameraMode;
    reviews: FilteredReviewCategory[];
    selectedDesignators: string[];
};
const initialState: ICopilotChatStore = {
    isShowingWelcomeMessage: true,
    isCreatingFirstMessage: false,

    canCreateThread: false,
    navbarTitle: undefined,
    isFluxOrganizationMember: false,
    newMessageInput: {
        text: "",
        model: getDefaultModel(false),
        selectedContexts: defaultSelectedContexts,
        focused: false,
    },
    userContext: {
        editorMode: EditorModes.schematic,
        cameraMode: CameraMode.two_d,
        selectedDesignators: [],
        reviews: [],
    },

    threads: {},
    draftMessages: {},
    threadsLoaded: false,
    currentThreadLoaded: false,
    knowledgeBaseSuggestions: [],
    knowledgeBaseSuggestionsLoading: false,
    knowledgeBaseSuggestionsSubscription: null,
    currentThreadUid: undefined,
    pendingThreadUidFromUrl: undefined,
    pendingMessageUidFromUrl: undefined,
    threadMessagesReactionsSubscription: {},
};

export const createCopilotChatStore = (
    dependencies: ICopilotChatStoreDependencies, // Additional dependencies for actions
): UseCopilotChatStore => {
    const {logger, chatRepository, documentRepository, knowledgeBaseRepository, currentUserService} = dependencies;

    return createWithEqualityFn<CopilotChatStoreState>()(
        /**
         * IMPORTANT: do not remove mutative here unless you know what you're doing. We use memoized selectors
         * on this store which depend on object references changing when any sub data changes.
         */
        devtools(
            mutative((set, get) => ({
                ...initialState,

                // API
                // NOTE: this sets all thread metadata, e.g. thread titles
                setCurrentDocumentUid: setCurrentDocumentUid(set, get, chatRepository, logger),
                setIsFluxOrganizationMember: setIsFluxOrganizationMember(set),

                // Threads
                setCurrentThreadToLatest: setCurrentThreadToLatest(set, get),
                setCurrentThread: setCurrentThread(set, get, chatRepository, logger),
                setPendingThreadFromUrl: (threadUid: string | undefined) => {
                    set((draft) => {
                        draft.pendingThreadUidFromUrl = threadUid;
                    });
                },
                clearPendingThreadFromUrl: () => {
                    set((draft) => {
                        draft.pendingThreadUidFromUrl = undefined;
                    });
                },
                setPendingMessageFromUrl: (messageUid: string | undefined) => {
                    set((draft) => {
                        draft.pendingMessageUidFromUrl = messageUid;
                    });
                },
                clearPendingMessageFromUrl: () => {
                    set((draft) => {
                        draft.pendingMessageUidFromUrl = undefined;
                    });
                },
                setCurrentThreadLoaded(loaded) {
                    set(
                        (draft) => {
                            draft.currentThreadLoaded = loaded;

                            const currentThreadUid = get().currentThreadUid;
                            if (loaded) {
                                if (!currentThreadUid) return;

                                if (draft.isCreatingFirstMessage) return;

                                const currentThreadMessages =
                                    createSortedThreadMessagesSelector(currentThreadUid)(draft);
                                const currentThreadIsEmpty = currentThreadMessages.length === 0;
                                draft.isShowingWelcomeMessage = currentThreadIsEmpty;
                                if (!currentThreadIsEmpty) {
                                    draft.canCreateThread = true;
                                }
                            } else {
                                draft.isShowingWelcomeMessage = !currentThreadUid;
                            }
                        },
                        undefined,
                        "setCurrentThreadLoaded",
                    );
                },
                createThread: createThread(set, get, currentUserService, chatRepository, documentRepository, logger),
                updateThreadName: updateThreadName(set, get, chatRepository, logger),
                deleteThread: deleteThread(set, get, chatRepository, logger),
                setThreads: (threads) => {
                    set(
                        (draft) => {
                            draft.threads = arrayToMapByUid(threads);
                        },
                        undefined,
                        "setThreads",
                    );
                },

                // Messages
                createMessage: createMessage(set, get, currentUserService, chatRepository, logger),
                setDraftMessage: (threadUid, text) => {
                    set((state) => {
                        const prev = state.draftMessages[threadUid];
                        state.draftMessages[threadUid] = {
                            ...prev,
                            text,
                        };
                    });
                },
                setDraftMessageModel: (threadUid, model) => {
                    set((state) => {
                        const prev = state.draftMessages[threadUid] ?? {text: ""};
                        state.draftMessages[threadUid] = {
                            ...prev,
                            model,
                        };
                    });
                },
                setDraftMessageContexts: (threadUid, contexts) => {
                    set((state) => {
                        const prev = state.draftMessages[threadUid] ?? {text: ""};
                        state.draftMessages[threadUid] = {
                            ...prev,
                            selectedContexts: contexts,
                        };
                    });
                },
                setDraftMessageFocused: (threadUid, focused) => {
                    set((state) => {
                        const prev = state.draftMessages[threadUid] ?? {text: ""};
                        state.draftMessages[threadUid] = {
                            ...prev,
                            focused,
                        };
                    });
                },
                setDraftMessagePromptVersion: (threadUid, promptVersion) => {
                    set((state) => {
                        const prev = state.draftMessages[threadUid] ?? {text: ""};
                        state.draftMessages[threadUid] = {
                            ...prev,
                            promptVersion,
                        };
                    });
                },
                branchMessage: branchMessage(set, get, currentUserService, chatRepository, logger),
                deleteMessage: deleteMessage(set, get, chatRepository, logger),
                setActionCompleted: setActionExecuted(set, get, chatRepository, logger),
                setActionExecuting: setActionExecuting(set, get, chatRepository, logger),
                resetActionExecution: resetActionExecution(set, get, chatRepository, logger),
                executeMessageActions: executeMessageActions(set, get, {
                    ...dependencies,
                    logger,
                    currentUserService,
                }),
                stopMessageStreaming: async (threadUid: string) => {
                    const {currentDocumentUid, threads} = get();

                    const originalThread = threads[threadUid]; // Original thread for rollback

                    // Early exit if prerequisites are not met
                    if (!currentDocumentUid || !originalThread) return;

                    let updatedThread;

                    set((draft) => {
                        const thread = draft.threads[threadUid];
                        if (!thread) return;

                        const streamingCopilotMessage = values(thread.messages).find(
                            (msg) => msg.meta_data.type === "copilot" && msg.meta_data.message_status === "streaming",
                        );

                        if (!streamingCopilotMessage) return;
                        if (streamingCopilotMessage.meta_data.type !== "copilot") return;

                        streamingCopilotMessage.meta_data.cancel_streaming_at_position =
                            streamingCopilotMessage.content.length;
                        streamingCopilotMessage.updated_at = new Date().getTime();
                        thread.updated_at = new Date().getTime();

                        updatedThread = thread;
                    });

                    if (!updatedThread) {
                        throw new Error("Thread not found");
                    }

                    try {
                        // Use updateThread to only update updated_at without overwriting
                        // backend-written fields like latest_brief.executionId and latest_brief.executionUpdatedAt
                        await chatRepository.updateThread(currentDocumentUid, threadUid, {
                            updated_at: Date.now(),
                        });
                    } catch (error) {
                        logger.error("Failed to update thread:", error);

                        // Rollback the optimistic update on failure
                        set((draft) => {
                            draft.threads[threadUid] = originalThread;
                        });
                    }
                },
                makeCurrentMessageBranch: (threadUid: string, messageUid: string) =>
                    set((draft) => {
                        const thread = draft.threads[threadUid];
                        if (!thread) return;

                        const message = thread.messages[messageUid];
                        if (!message) return;

                        message.updated_at = new Date().getTime();
                        thread.updated_at = new Date().getTime();
                    }),

                // Reactions
                setMessageReaction: setMessageReaction(set, get, currentUserService, chatRepository, logger),
                removeMessageReaction: removeMessageReaction(set, get, currentUserService, chatRepository, logger),

                fetchKnowledgeBaseSuggestions: fetchKnowledgeBaseSuggestions(
                    set,
                    get,
                    currentUserService,
                    knowledgeBaseRepository,
                    logger,
                ),
                subscribeToKnowledgeBaseSuggestions: subscribeToKnowledgeBaseSuggestions(
                    set,
                    get,
                    currentUserService,
                    knowledgeBaseRepository,
                    logger,
                ),
                approveKnowledgeBaseSuggestion: approveKnowledgeBaseSuggestion(
                    set,
                    get,
                    currentUserService,
                    knowledgeBaseRepository,
                    logger,
                ),
                declineKnowledgeBaseSuggestion: declineKnowledgeBaseSuggestion(
                    set,
                    get,
                    knowledgeBaseRepository,
                    logger,
                ),
                updateKnowledgeBaseSuggestion: updateKnowledgeBaseSuggestion(set, get, knowledgeBaseRepository, logger),
                setKnowledgeBaseSuggestionsSubscription: setKnowledgeBaseSuggestionsSubscription(set),

                setNewMessageInput(params) {
                    // TODO: Can't we just merge these? This is really confusing since new properties have to be added here manually.
                    const text = params.text;
                    if (text !== undefined) {
                        const currentThreadUid = get().currentThreadUid;
                        if (currentThreadUid) {
                            get().setDraftMessage(currentThreadUid, text);
                        }

                        set((draft) => {
                            draft.newMessageInput.text = text;
                        });
                    }

                    const model = params.model;
                    if (model !== undefined) {
                        set((draft) => {
                            draft.newMessageInput.model = model;
                        });
                    }

                    const selectedContexts = params.selectedContexts;
                    if (selectedContexts !== undefined) {
                        set((draft) => {
                            draft.newMessageInput.selectedContexts = selectedContexts;
                        });
                    }

                    const focused = params.focused;
                    if (focused !== undefined) {
                        set((draft) => {
                            draft.newMessageInput.focused = focused;
                        });
                    }

                    const promptVersion = params.promptVersion;
                    if (promptVersion !== undefined) {
                        set((draft) => {
                            draft.newMessageInput.promptVersion = promptVersion;
                        });
                    }

                    const useSkipper = params.useSkipper;
                    if (useSkipper !== undefined) {
                        set((draft) => {
                            draft.newMessageInput.useSkipper = useSkipper;
                        });
                    }

                    const useOrbit = params.useOrbit;
                    if (useOrbit !== undefined) {
                        set((draft) => {
                            draft.newMessageInput.useOrbit = useOrbit;
                        });
                    }
                },
                handleUserClickedExamplePrompt(prompt: string) {
                    get().setNewMessageInput({text: prompt, focused: true});
                },
                updatedUserContext: updatedUserContext(set, get),
                async sendCurrentMessage() {
                    const {text, model, selectedContexts, promptVersion} = get().newMessageInput;
                    const currentThreadUid = get().currentThreadUid;

                    if (!text) return;

                    // Prevent sending new messages while streaming is active
                    if (currentThreadUid && createIsThreadStreamingSelector(currentThreadUid)(get())) {
                        return;
                    }

                    // Use the selected model directly (GPT-5 general model is now the default behavior)
                    const selectedModel = model;

                    // TODO: should we extract file attachments too?
                    const selectedTools = getToolsFromMessage(text);
                    const {editorMode, cameraMode, reviews, selectedDesignators} = get().userContext;
                    const metaData: IChatMessageHumanMetaData = {
                        type: "human" as const,
                        selected_model: selectedModel,
                        selected_contexts: selectedContexts,
                        selected_tools: selectedTools,
                        prompt_version: promptVersion,
                        editor_mode: editorMode,
                        pcb_camera_mode: cameraMode,
                        // reviews object sometimes has empty keys, which is not valid to persist to firebase
                        reviews: reviews.map((r) => replaceEmptyKeys(r, "empty")),
                        selected_object_designators: selectedDesignators,
                    };

                    const {currentDocumentUid} = get();

                    if (currentThreadUid) {
                        // Check if this is the first message to an "Untitled Thread" and rename it
                        const currentThread = get().threads[currentThreadUid];
                        const shouldRenameThread =
                            currentThread?.namedBy === "initializer" && isUntitledThreadName(currentThread?.name);

                        if (shouldRenameThread && currentDocumentUid) {
                            const newName = threadNameFromMessageText(text);

                            // Optimistically update the local state
                            set((draft) => {
                                const draftThread = draft.threads[currentThreadUid];
                                if (draftThread) {
                                    draftThread.name = newName;
                                    draftThread.updated_at = Date.now();
                                    // Keep namedBy as "initializer" so backend can still replace with LLM-generated title
                                }
                            });

                            // Persist to backend (fire and forget, don't block message creation)
                            // Use updateThread to only update specific fields without overwriting
                            // backend-written fields like latest_brief.executionId and latest_brief.executionUpdatedAt
                            void chatRepository.updateThread(currentDocumentUid, currentThreadUid, {
                                name: newName,
                                updated_at: Date.now(),
                                // Keep namedBy as "initializer" so backend can still replace with LLM-generated title
                            });
                        }

                        get().createMessage(currentThreadUid, text, metaData);
                        get().setNewMessageInput({text: "", selectedContexts: defaultSelectedContexts});
                    } else {
                        const newThreadUid = guid();
                        set((draft) => {
                            draft.isShowingWelcomeMessage = false;
                            draft.isCreatingFirstMessage = true;
                            // Set currentThreadUid synchronously so the UI can update immediately.
                            // Note: We set this here instead of calling setCurrentThread() because
                            // setCurrentThread() also sets up a subscription to the thread's messages,
                            // which would fail if called before the thread exists in state.
                            // createThread() will call setCurrentThread() after adding the thread to state,
                            // which will properly set up the subscription.
                            draft.currentThreadUid = newThreadUid;
                        });

                        const document = dependencies.documentStore.getState().document;
                        const planningDisabled = document?.newThreadPlanningDisabled;

                        await get().createThread(newThreadUid, text, planningDisabled);
                        await get().createMessage(newThreadUid, text, metaData);

                        set((draft) => {
                            draft.isCreatingFirstMessage = false;
                        });

                        get().setNewMessageInput({
                            text: "",
                            model: getDefaultModel(get().isFluxOrganizationMember),
                            selectedContexts: defaultSelectedContexts,
                            promptVersion: undefined,
                        });

                        return newThreadUid;
                    }
                },

                handleBackendUpdatedThreads(addedOrChangedThreads) {
                    handleBackendUpdatedThreads(set, get, addedOrChangedThreads);
                },
                clear() {
                    set({...initialState});
                },
            })),
            {enabled: isDevEnv() && !areWeTestingWithJest(), name: "Copilot Chat Store"},
        ),
    );
};

export type UseCopilotChatStore = UseBoundStore<StoreApi<CopilotChatStoreState>>;

export const createCopilotChatStoreHook = (
    dependencies: ICopilotChatStoreDependencies, // Additional dependencies for actions
): UseCopilotChatStore => {
    return createCopilotChatStore(dependencies);
};
